import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle, Clock } from 'lucide-react';

// Candidate-facing completion screen.
// Intentionally shows NO scores, recommendations, axis breakdowns, or feedback —
// reports are HR-only. Candidates see "done, we'll be in touch".

const TestCompletePage = () => {
  const location = useLocation();
  const { type, stageLabel, isExam } = location.state || {};
  const [closeBlocked, setCloseBlocked] = useState(false);

  // Browsers block window.close() for tabs the user opened themselves.
  // Try it once — if the tab is still here after a beat, show a hint
  // telling the candidate how to close it manually.
  const handleClose = () => {
    try { window.close(); } catch { /* fall through */ }
    // If we're still mounted ~120ms later, the close was blocked.
    setTimeout(() => setCloseBlocked(true), 120);
  };

  const closeHint = navigator.platform?.toLowerCase().includes('mac')
    ? 'Press ⌘W to close this tab.'
    : 'Press Ctrl+W to close this tab.';

  const getTestTypeLabel = (type) => {
    // If a specific stage label was passed (e.g. from a pipeline exam stage), use it directly
    if (stageLabel) return stageLabel;
    switch (type) {
      case 'conversational_prescreening': return 'Pre-Screening Interview';
      case 'psychoanalytical': return 'Psychoanalytical Assessment';
      case 'final_interview':
      case 'finalInterview': return 'Final Interview';
      default: return 'Assessment';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-red-200 flex items-center justify-center px-4 py-8">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full border-4 border-green-200 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {getTestTypeLabel(type)} Completed
          </h1>

          <p className="text-gray-600 max-w-md mx-auto">
            Thank you for your time. Your {isExam ? 'answers have been submitted and will be reviewed' : 'responses have been recorded and shared with the hiring team'}.
          </p>
        </div>

        {/* Next Steps — no scores, no feedback */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
            <Clock className="w-5 h-5 mr-2" />
            What happens next
          </h3>
          <div className="text-sm text-gray-600 space-y-2">
            <p>• The hiring team will review your {isExam ? 'assessment' : 'interview'} within 3-5 business days.</p>
            <p>• If you move forward, you will receive an email invitation for the next stage.</p>
            <p>• You can now safely close this tab.</p>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleClose}
            className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            Close tab
          </button>
        </div>
        {closeBlocked && (
          <p className="mt-3 text-center text-xs text-gray-500">
            Your browser blocked auto-close — {closeHint}
          </p>
        )}
      </div>
    </div>
  );
};

export default TestCompletePage;
